import type { Signal } from '@angular/core';
import { computed, signal } from '@angular/core';
import type { Sort } from '@angular/material/sort';

export interface SortItem<T extends string = string> {
    field: T;
    direction: 'asc' | 'desc';
}

/** Return type for createSort().active() */
export interface SortActive {
    active: string;
    direction: 'asc' | 'desc';
}

/** Return type of createSort factory */
export interface SortState<T extends string = string> {
    list: Signal<SortItem<T>[]>;
    active: Signal<SortActive>;
    /**
     * Serializing API-style sort strings.
     * Format: `sort=name,-createdAt` where `-` prefix indicates descending.
     */
    serialise: Signal<string>;
    apply: (sort: Sort) => void;
    reset: () => void;
}

/**
 * Creates a sort state object with allowed fields, list signal, and helpers.
 * List is read-only; use apply() and reset() to mutate.
 *
 * @param allowed - List of field names that may be sorted (others are rejected by apply)
 */
export function createSort<T extends string>(
    allowed: readonly T[]
): SortState<T> {
    const list = signal<SortItem<T>[]>([]);

    const active = computed<SortActive>(() => {
        const items = list();
        const first = items[0];
        if (!first) {
            return { active: '', direction: 'asc' as const };
        }
        return {
            active: first.field,
            direction: first.direction,
        };
    });

    const serialise = computed(() => {
        return list()
            .map(({ field, direction }) =>
                direction === 'desc' ? `-${field}` : field
            )
            .join(',');
    });

    function apply(sort: Sort): void {
        if (!allowed.includes(sort.active as T)) return;

        const { active: clickedActive, direction } = sort;
        if (direction === '') {
            list.update((current) =>
                current.filter((item) => item.field !== clickedActive)
            );
            return;
        }

        list.update((current) => {
            const without = current.filter(
                (item) => item.field !== clickedActive
            );
            return [
                {
                    field: clickedActive as T,
                    direction: direction as 'asc' | 'desc',
                },
                ...without,
            ];
        });
    }

    function reset(): void {
        list.set([]);
    }

    return {
        list,
        active,
        serialise,
        apply,
        reset,
    };
}
